import { ExternalReferencesMapper, KeysMapper, ReferencesMapper } from "../models/mappers";
import { accountExternalReferences, accountFields, accountKey, accountReferences } from "./account";
import { annotationFields, annotationKey, annotationReferences } from "./annotation";
import { contactFields, contactKey, contactReferences } from "./contact";
import { EntityName } from "./entityNames";
import { autoRequestFields, autoRequestKey, autoRequestReferences } from "./gaming_autorequest";
import { autoRequestTopicFields, autoRequestTopicKey } from "./gaming_autorequesttopic";
import {
  contractAutoConfigFields,
  contractAutoConfigKey,
  contractAutoConfigReferences,
} from "./gaming_contractautoconfig";
import {
  contractRegionFields,
  contractRegionKey,
  contractRegionReferences,
} from "./gaming_contractregion";
import { contractSubTypeFields, contractSubTypeKey } from "./gaming_contractsubtype";
import { contractTypeFields, contractTypeKey, contractTypeReferences } from "./gaming_contracttype";
import {
  ecommerceAddressFields,
  ecommerceAddressKey,
  ecommerceAddressReferences,
} from "./gaming_ecommaddress";
import { ecommerceFields, ecommerceKey, ecommerceReferences } from "./gaming_ecommerce";
import {
  ecommerceFileFields,
  ecommerceFileKey,
  ecommerceFileReferences,
} from "./gaming_ecommercefile";
import { gameConceptFields, gameConceptKey, gameConceptReferences } from "./gaming_gameconcept";
import {
  hubInvitationFields,
  hubInvitationKey,
  hubInvitationReferences,
} from "./gaming_hubinvitation";
import { initiativesFields, initiativesKey, initiativesReferences } from "./gaming_initiatives";
import { msSignerFields, msSignerKey } from "./gaming_mssigner";
import {
  onboardingExternalReferences,
  onboardingFields,
  onboardingKey,
  onboardingReferences,
} from "./gaming_onboarding";
import {
  onboardingHubProfileExternalReferences,
  onboardingHubProfileFields,
  onboardingHubProfileKey,
  onboardingHubProfileReferences,
} from "./gaming_onboardinghubprofile";
import {
  pitchExternalReferences,
  pitchFields,
  pitchKey,
  pitchReferences,
} from "./gaming_pitchprogram";
import {
  partnerInquiryFields,
  partnerInquiryKey,
  partnerInquiryReferences,
} from "./gaming_partnerinquiry";
import { programFields, programKey, programReferences } from "./gaming_program";
import { streamRegFields, streamRegKey, streamRegReferences } from "./gaming_streamreg";
import {
  contractRequestFields,
  contractRequestKey,
  contractRequestReferences,
} from "./ggp_contractrequest";
import { countryFields, countryKey } from "./ggp_country";
import { ggpFields, strikeConceptKey, strikeConceptReferences } from "./ggp_strike";
import { ticketLanguageFields, ticketLanguageKey } from "./ticket_language";
import { ticketFields, ticketKey } from "./ticket_ticket";
import { ticketAttachmentFields, ticketAttachmentKey } from "./ticket_ticketattachment";
import { ticketCommentFields, ticketCommentKey } from "./ticket_ticketcomment";
import { ticketContactFields, ticketContactKey } from "./ticket_ticketcontact";
import { ticketPartnerFields, ticketPartnerKey } from "./ticket_ticketpartner";
import { ticketProductFields, ticketProductKey } from "./ticket_ticketproduct";
import { ticketProductFeatureFields, ticketProductFeatureKey } from "./ticket_ticketproductfeature";
import { ticketTeamFields, ticketTeamKey } from "./ticket_ticketteam";
import { ticketInvitationFields, ticketInvitationKey } from "./ticket_ticketteaminvitation";
import { ticketTeamMemberFields, ticketTeamMemberKey } from "./ticket_ticketteammember";
import { userFields, userKey } from "./user";

export const internalTypeNameKey = "__lmn_type_key";

// Entities are modeled using metadata and type inference to generalize their
// types and the logic to read and write to CRM.
//
// To add a new CRM entity start by adding the entity name to EntityName, this will
// result in build errors since the mappers and generic functions can't infer
// its metadata. To fix this add the needed metadata for the entity metadata,
// a good example is "../metadata/account.ts", then update the mappers located
// in "../metadata/mappers.ts". Once done is possible to use providers to read
// and write data just the same as for other types, for example by calling
// getEntities(EntityName.account) which will return a strongly typed instance
//  from CRM that matches the metadata.
//
// To update an existing model just go to the corresponding metadata file and
// update whatever is needed.

export enum OptionSet {
  ggp_approvalstatus_optionset = "ggp_approvalstatus_optionset",
  ggp_yesnonull = "ggp_yesnonull",
  ggp_localplayers_optionset = "ggp_localplayers_optionset",
  ggp_playernumber = "ggp_playernumber",
  ggp_genres = "ggp_genres",
  ggp_middlewareengine_optionset = "ggp_middlewareengine_optionset",
  ggp_esrbratings = "ggp_esrbratings",
  ggp_germanyratings = "ggp_germanyratings",
  gaming_taiwancsrrratings = "gaming_taiwancsrrratings",
  ggp_europeratings = "ggp_europeratings",
  ggp_australiaratings = "ggp_australiaratings",
  gaming_russiapcbpratings = "gaming_russiapcbpratings",
  ggp_japanratings = "ggp_japanratings",
  ggp_korearatings = "ggp_korearatings",
  ggp_saudiarabiaratings = "ggp_saudiarabiaratings",
  ggp_brazilratings = "ggp_brazilratings",
  gaming_newzelandoflcratings = "gaming_newzelandoflcratings",
  gaming_3dspatialsound = "gaming_3dspatialsound",
}

export type AnyEntityField<T extends EntityName> = (typeof metadata.fields)[T][number];

export type CrmEntity<T extends EntityName> = {
  "@odata.etag"?: string;
} & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [_key in (typeof metadata.fields)[T][number]]: any;
};

export enum FilterOperator {
  Equals = "eq",
  NotEquals = "ne",
  GreaterThan = "gt",
  LessThan = "lt",
  GreaterOrEqualThan = "ge",
  LessOrEqualThan = "le",
}

export type FilterElement<T extends EntityName> = {
  field: AnyEntityField<T>;
  operator: FilterOperator;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
};

const fieldsMapper = {
  [EntityName.annotation]: annotationFields,
  [EntityName.contact]: contactFields,
  [EntityName.account]: accountFields,
  [EntityName.gaming_onboardinghubprofile]: onboardingHubProfileFields,
  [EntityName.gaming_onboarding]: onboardingFields,
  [EntityName.gaming_hubinvitation]: hubInvitationFields,
  [EntityName.ggp_country]: countryFields,
  [EntityName.gaming_gameconcept]: gameConceptFields,
  [EntityName.gaming_ecommerce]: ecommerceFields,
  [EntityName.gaming_ecommercefile]: ecommerceFileFields,
  [EntityName.gaming_ecommaddress]: ecommerceAddressFields,
  [EntityName.ggp_contractrequest]: contractRequestFields,
  [EntityName.gaming_contracttype]: contractTypeFields,
  [EntityName.user]: userFields,
  [EntityName.gaming_contractsubtype]: contractSubTypeFields,
  [EntityName.gaming_pitchprogram]: pitchFields,
  [EntityName.ggp_strike]: ggpFields,
  [EntityName.gaming_streamreg]: streamRegFields,
  [EntityName.gaming_contractregion]: contractRegionFields,
  [EntityName.gaming_mssigner]: msSignerFields,
  [EntityName.gaming_contractautoconfig]: contractAutoConfigFields,
  [EntityName.gaming_autorequesttopic]: autoRequestTopicFields,
  [EntityName.gaming_autorequest]: autoRequestFields,
  [EntityName.gaming_program]: programFields,
  [EntityName.gaming_initiatives]: initiativesFields,
  [EntityName.gaming_partnerinquiry]: partnerInquiryFields,
  [EntityName.ticket_ticket]: ticketFields,
  [EntityName.ticket_ticketattachment]: ticketAttachmentFields,
  [EntityName.ticket_ticketcomment]: ticketCommentFields,
  [EntityName.ticket_ticketcontact]: ticketContactFields,
  [EntityName.ticket_ticketpartner]: ticketPartnerFields,
  [EntityName.ticket_ticketteam]: ticketTeamFields,
  [EntityName.ticket_ticketteammember]: ticketTeamMemberFields,
  [EntityName.ticket_ticketteaminvitation]: ticketInvitationFields,
  [EntityName.ticket_ticketproduct]: ticketProductFields,
  [EntityName.ticket_ticketproductfeature]: ticketProductFeatureFields,
  [EntityName.ticket_language]: ticketLanguageFields,
} as const;

const referencesMapper: ReferencesMapper = {
  [EntityName.annotation]: annotationReferences,
  [EntityName.contact]: contactReferences,
  [EntityName.account]: accountReferences,
  [EntityName.gaming_onboardinghubprofile]: onboardingHubProfileReferences,
  [EntityName.gaming_onboarding]: onboardingReferences,
  [EntityName.gaming_hubinvitation]: hubInvitationReferences,
  [EntityName.ggp_country]: undefined,
  [EntityName.gaming_gameconcept]: gameConceptReferences,
  [EntityName.gaming_ecommerce]: ecommerceReferences,
  [EntityName.gaming_ecommercefile]: ecommerceFileReferences,
  [EntityName.gaming_ecommaddress]: ecommerceAddressReferences,
  [EntityName.ggp_contractrequest]: contractRequestReferences,
  [EntityName.gaming_contracttype]: contractTypeReferences,
  [EntityName.user]: undefined,
  [EntityName.gaming_contractsubtype]: undefined,
  [EntityName.gaming_pitchprogram]: pitchReferences,
  [EntityName.ggp_strike]: strikeConceptReferences,
  [EntityName.gaming_streamreg]: streamRegReferences,
  [EntityName.gaming_contractregion]: contractRegionReferences,
  [EntityName.gaming_mssigner]: undefined,
  [EntityName.gaming_contractautoconfig]: contractAutoConfigReferences,
  [EntityName.gaming_autorequesttopic]: undefined,
  [EntityName.gaming_autorequest]: autoRequestReferences,
  [EntityName.gaming_program]: programReferences,
  [EntityName.gaming_initiatives]: initiativesReferences,
  [EntityName.gaming_partnerinquiry]: partnerInquiryReferences,
  [EntityName.ticket_ticket]: undefined,
  [EntityName.ticket_ticketattachment]: undefined,
  [EntityName.ticket_ticketcomment]: undefined,
  [EntityName.ticket_ticketcontact]: undefined,
  [EntityName.ticket_ticketpartner]: undefined,
  [EntityName.ticket_ticketteam]: undefined,
  [EntityName.ticket_ticketteammember]: undefined,
  [EntityName.ticket_ticketteaminvitation]: undefined,
  [EntityName.ticket_ticketproduct]: undefined,
  [EntityName.ticket_ticketproductfeature]: undefined,
  [EntityName.ticket_language]: undefined,
};

const externalReferencesMapper: ExternalReferencesMapper = {
  [EntityName.annotation]: undefined,
  [EntityName.contact]: undefined,
  [EntityName.account]: accountExternalReferences,
  [EntityName.gaming_onboardinghubprofile]: onboardingHubProfileExternalReferences,
  [EntityName.gaming_onboarding]: onboardingExternalReferences,
  [EntityName.gaming_hubinvitation]: undefined,
  [EntityName.ggp_country]: undefined,
  [EntityName.gaming_gameconcept]: undefined,
  [EntityName.gaming_ecommerce]: undefined,
  [EntityName.gaming_ecommercefile]: undefined,
  [EntityName.gaming_ecommaddress]: undefined,
  [EntityName.ggp_contractrequest]: undefined,
  [EntityName.gaming_contracttype]: undefined,
  [EntityName.user]: undefined,
  [EntityName.gaming_contractsubtype]: undefined,
  [EntityName.gaming_pitchprogram]: pitchExternalReferences,
  [EntityName.ggp_strike]: undefined,
  [EntityName.gaming_streamreg]: undefined,
  [EntityName.gaming_contractregion]: undefined,
  [EntityName.gaming_mssigner]: undefined,
  [EntityName.gaming_contractautoconfig]: undefined,
  [EntityName.gaming_autorequesttopic]: undefined,
  [EntityName.gaming_autorequest]: undefined,
  [EntityName.gaming_program]: undefined,
  [EntityName.gaming_initiatives]: undefined,
  [EntityName.gaming_partnerinquiry]: undefined,
  [EntityName.ticket_ticket]: undefined,
  [EntityName.ticket_ticketattachment]: undefined,
  [EntityName.ticket_ticketcomment]: undefined,
  [EntityName.ticket_ticketcontact]: undefined,
  [EntityName.ticket_ticketpartner]: undefined,
  [EntityName.ticket_ticketteam]: undefined,
  [EntityName.ticket_ticketteammember]: undefined,
  [EntityName.ticket_ticketteaminvitation]: undefined,
  [EntityName.ticket_ticketproduct]: undefined,
  [EntityName.ticket_ticketproductfeature]: undefined,
  [EntityName.ticket_language]: undefined,
};

const keysMapper: KeysMapper = {
  [EntityName.annotation]: annotationKey,
  [EntityName.contact]: contactKey,
  [EntityName.account]: accountKey,
  [EntityName.gaming_onboardinghubprofile]: onboardingHubProfileKey,
  [EntityName.gaming_onboarding]: onboardingKey,
  [EntityName.gaming_hubinvitation]: hubInvitationKey,
  [EntityName.ggp_country]: countryKey,
  [EntityName.gaming_gameconcept]: gameConceptKey,
  [EntityName.gaming_ecommerce]: ecommerceKey,
  [EntityName.gaming_ecommercefile]: ecommerceFileKey,
  [EntityName.gaming_ecommaddress]: ecommerceAddressKey,
  [EntityName.ggp_contractrequest]: contractRequestKey,
  [EntityName.gaming_contracttype]: contractTypeKey,
  [EntityName.user]: userKey,
  [EntityName.gaming_contractsubtype]: contractSubTypeKey,
  [EntityName.gaming_pitchprogram]: pitchKey,
  [EntityName.ggp_strike]: strikeConceptKey,
  [EntityName.gaming_streamreg]: streamRegKey,
  [EntityName.gaming_contractregion]: contractRegionKey,
  [EntityName.gaming_mssigner]: msSignerKey,
  [EntityName.gaming_contractautoconfig]: contractAutoConfigKey,
  [EntityName.gaming_autorequesttopic]: autoRequestTopicKey,
  [EntityName.gaming_autorequest]: autoRequestKey,
  [EntityName.gaming_program]: programKey,
  [EntityName.gaming_initiatives]: initiativesKey,
  [EntityName.gaming_partnerinquiry]: partnerInquiryKey,
  [EntityName.ticket_ticket]: ticketKey,
  [EntityName.ticket_ticketattachment]: ticketAttachmentKey,
  [EntityName.ticket_ticketcomment]: ticketCommentKey,
  [EntityName.ticket_ticketcontact]: ticketContactKey,
  [EntityName.ticket_ticketpartner]: ticketPartnerKey,
  [EntityName.ticket_ticketteam]: ticketTeamKey,
  [EntityName.ticket_ticketteammember]: ticketTeamMemberKey,
  [EntityName.ticket_ticketteaminvitation]: ticketInvitationKey,
  [EntityName.ticket_ticketproduct]: ticketProductKey,
  [EntityName.ticket_ticketproductfeature]: ticketProductFeatureKey,
  [EntityName.ticket_language]: ticketLanguageKey,
};

export const metadata = {
  fields: fieldsMapper,
  references: referencesMapper,
  externalReferences: externalReferencesMapper,
  keys: keysMapper,
} as const;
